/**
 * Structured bilingual (EN / HE) CLASS CHANGE guides for MU BEGINNING.
 * Player-facing only — no internal item / monster / quest index numbers in the rendered text.
 *
 * Topics: "class-1" (Sebina — first class change), "class-2" (Marlon — Hero & Combo bridge),
 * "class-3" (Apostle Devin — third class change).
 *
 * Block types consumed by Guides.jsx:
 *   p | h | note | warn · table · npc · itemcard · mix · timeline · results
 */

import { guideImg } from "./guideAssets";

export const guideClassChangeContent = {
  "class-1": [
    {
      type: "h",
      en: "First Class Change — Sebina the Priest",
      he: "שינוי מחלקה ראשון — Sebina the Priest",
    },
    {
      type: "p",
      en: "The first class change is done through two quests given by Sebina the Priest. Finish both to promote your character.",
      he: "שינוי המחלקה הראשון נעשה דרך שני קווסטים שנותנת Sebina the Priest. סיימו את שניהם כדי לקדם את הדמות.",
    },
    {
      type: "npc",
      img: guideImg.npcSebina,
      name: "Sebina the Priest",
      map: { en: "Devias", he: "Devias" },
      coords: "183, 32",
      note: {
        en: "Also available in Arkania.",
        he: "זמינה גם ב־Arkania.",
      },
    },
    {
      type: "note",
      en: "Level requirement: 150–400.",
      he: "דרישת רמה: 150–400.",
    },

    {
      type: "h",
      en: "Quest 0 — Scroll of the Emperor",
      he: "קווסט 0 — Scroll of the Emperor",
    },
    {
      type: "p",
      en: "Pay 10,000 Zen to start. Hunt monsters level 45–60 until a Scroll of the Emperor drops (about 10% while the quest is active), then return it to Sebina. Useful maps/monsters: Atlans (Vepar, Valkyrie), Dungeon (Poison Bull Fighter, Dark Knight), and Kalima areas with matching levels.",
      he: "שלמו 10,000 Zen כדי להתחיל. צודו מפלצות ברמה 45–60 עד ש־Scroll of the Emperor נופל (בערך 10% בזמן שהקווסט פעיל), ואז החזירו אותו ל־Sebina. מפות/מפלצות שימושיות: Atlans (Vepar, Valkyrie), Dungeon (Poison Bull Fighter, Dark Knight), ואזורי Kalima ברמות מתאימות.",
    },
    {
      type: "itemcard",
      img: guideImg.scrollEmperor,
      name: "Scroll of the Emperor",
      qty: "×1",
      note: {
        en: "Drops from monsters level 45–60 — about 10% while the quest is active. e.g. Atlans (Vepar, Valkyrie), Dungeon (Poison Bull Fighter, Dark Knight), Kalima (matching levels).",
        he: "נופל ממפלצות ברמה 45–60 — בערך 10% בזמן שהקווסט פעיל. לדוגמה Atlans (Vepar, Valkyrie), Dungeon (Poison Bull Fighter, Dark Knight), Kalima (רמות מתאימות).",
      },
    },

    {
      type: "h",
      en: "Quest 1 — Your class item",
      he: "קווסט 1 — פריט המחלקה שלכם",
    },
    {
      type: "p",
      en: "Pay 20,000 Zen. Hunt monsters level 62–76 until your class item drops (about 10%), then return it to Sebina. Useful maps/monsters: Atlans (Great Bahamut, Silver Valkyrie, Lizard King), Tarkan (Mutant, Bloody Wolf), Aida (Death Tree, Forest Orc).",
      he: "שלמו 20,000 Zen. צודו מפלצות ברמה 62–76 עד שפריט המחלקה שלכם נופל (בערך 10%), ואז החזירו אותו ל־Sebina. מפות/מפלצות שימושיות: Atlans (Great Bahamut, Silver Valkyrie, Lizard King), Tarkan (Mutant, Bloody Wolf), Aida (Death Tree, Forest Orc).",
    },
    {
      type: "itemcard",
      img: guideImg.brokenSword,
      name: "Broken Sword",
      qty: "×1",
      note: {
        en: "For Dark Knight (DK), Magic Gladiator (MG), Dark Lord (DL) and Rage Fighter (RF).",
        he: "עבור Dark Knight (DK), Magic Gladiator (MG), Dark Lord (DL) ו־Rage Fighter (RF).",
      },
    },
    {
      type: "itemcard",
      img: guideImg.tearElf,
      name: "Tear of Elf",
      qty: "×1",
      note: {
        en: "For Fairy Elf (FE).",
        he: "עבור Fairy Elf (FE).",
      },
    },
    {
      type: "itemcard",
      img: guideImg.soulShard,
      name: "Soul Shard of Wizard",
      qty: "×1",
      note: {
        en: "For Dark Wizard (DW).",
        he: "עבור Dark Wizard (DW).",
      },
    },
    {
      type: "itemcard",
      img: guideImg.abyssalEye,
      name: "Abyssal Eye",
      qty: "×1",
      note: {
        en: "For Summoner (SU).",
        he: "עבור Summoner (SU).",
      },
    },

    {
      type: "h",
      en: "Result — First Class",
      he: "תוצאה — מחלקה ראשונה",
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classDwT1,
          name: { en: "Dark Wizard", he: "Dark Wizard" },
          label: { en: "Start", he: "התחלה" },
        },
        {
          img: guideImg.classDwT2,
          name: { en: "Soul Master", he: "Soul Master" },
          label: { en: "After Sebina", he: "אחרי Sebina" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classDkT1,
          name: { en: "Dark Knight", he: "Dark Knight" },
          label: { en: "Start", he: "התחלה" },
        },
        {
          img: guideImg.classDkT2,
          name: { en: "Blade Knight", he: "Blade Knight" },
          label: { en: "After Sebina", he: "אחרי Sebina" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classFeT1,
          name: { en: "Fairy Elf", he: "Fairy Elf" },
          label: { en: "Start", he: "התחלה" },
        },
        {
          img: guideImg.classFeT2,
          name: { en: "Muse Elf", he: "Muse Elf" },
          label: { en: "After Sebina", he: "אחרי Sebina" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classSuT1,
          name: { en: "Summoner", he: "Summoner" },
          label: { en: "Start", he: "התחלה" },
        },
        {
          img: guideImg.classSuT2,
          name: { en: "Bloody Summoner", he: "Bloody Summoner" },
          label: { en: "After Sebina", he: "אחרי Sebina" },
        },
      ],
    },
    {
      type: "table",
      columns: [
        { key: "from", en: "Class", he: "מחלקה" },
        { key: "to", en: "Becomes", he: "הופך ל־" },
      ],
      rows: [
        { from: "Dark Wizard (DW)", to: "Soul Master" },
        { from: "Dark Knight (DK)", to: "Blade Knight" },
        { from: "Fairy Elf (FE)", to: "Muse Elf" },
        { from: "Summoner (SU)", to: "Bloody Summoner" },
      ],
    },
    {
      type: "note",
      en: "Magic Gladiator (MG), Dark Lord (DL) and Rage Fighter (RF) complete these quests but do NOT receive a second-class promotion name here — they keep their base class until the third class change.",
      he: "Magic Gladiator (MG), Dark Lord (DL) ו־Rage Fighter (RF) משלימים את הקווסטים אך אינם מקבלים שם קידום מחלקה שנייה כאן — הם נשארים במחלקת הבסיס עד שינוי המחלקה השלישי.",
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classMgT1,
          name: { en: "Magic Gladiator", he: "Magic Gladiator" },
          label: { en: "No rename here", he: "ללא שינוי שם כאן" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classDlT1,
          name: { en: "Dark Lord", he: "Dark Lord" },
          label: { en: "No rename here", he: "ללא שינוי שם כאן" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classRfT1,
          name: { en: "Rage Fighter", he: "Rage Fighter" },
          label: { en: "No rename here", he: "ללא שינוי שם כאן" },
        },
      ],
    },
  ],

  "class-2": [
    {
      type: "h",
      en: "Second Stage — Marlon (Hero & Combo)",
      he: "שלב שני — Marlon (Hero ו־Combo)",
    },
    {
      type: "p",
      en: "Marlon is a bridge, not a class rename. His quests grant Hero Status and unlock the Combo skill — both are needed later for the Apostle Devin third class change.",
      he: "Marlon הוא גשר, לא שינוי שם מחלקה. הקווסטים שלו מעניקים Hero Status ופותחים את מיומנות ה־Combo — שניהם נחוצים בהמשך לשינוי המחלקה השלישי אצל Apostle Devin.",
    },
    {
      type: "npc",
      img: guideImg.npcMarlon,
      name: "Marlon",
      map: { en: "Devias", he: "Devias" },
      coords: "198, 47",
    },
    {
      type: "note",
      en: "Level requirement: 220–400. You must have finished Sebina's quests first.",
      he: "דרישת רמה: 220–400. חובה לסיים קודם את הקווסטים של Sebina.",
    },
    {
      type: "warn",
      en: "Marlon does not rename your class. He prepares you (Hero Status + Combo) for the third class change at Apostle Devin.",
      he: "Marlon לא משנה את שם המחלקה שלכם. הוא מכין אתכם (Hero Status + Combo) לשינוי המחלקה השלישי אצל Apostle Devin.",
    },

    {
      type: "h",
      en: "Quest 2 — Ring of Honor (Hero Status)",
      he: "קווסט 2 — Ring of Honor (Hero Status)",
    },
    {
      type: "p",
      en: "Pay 30,000 Zen. Hunt monsters level 72–108 until a Ring of Honor drops (about 10%), then return it to Marlon to gain Hero Status. Useful maps/monsters: Tarkan (Mutant, Bloody Wolf, Beam Knight, Death Beam Knight, Tantalos), Aida (Death Rider, Witch Queen), Crywolf (Lance Scout).",
      he: "שלמו 30,000 Zen. צודו מפלצות ברמה 72–108 עד ש־Ring of Honor נופל (בערך 10%), ואז החזירו אותו ל־Marlon כדי לקבל Hero Status. מפות/מפלצות שימושיות: Tarkan (Mutant, Bloody Wolf, Beam Knight, Death Beam Knight, Tantalos), Aida (Death Rider, Witch Queen), Crywolf (Lance Scout).",
    },
    {
      type: "itemcard",
      img: guideImg.ringHonor,
      name: "Ring of Honor",
      qty: "×1",
      note: {
        en: "Drops from monsters level 72–108 — about 10% while the quest is active. e.g. Tarkan (Mutant, Bloody Wolf, Beam Knight, Death Beam Knight, Tantalos), Aida (Death Rider, Witch Queen), Crywolf (Lance Scout).",
        he: "נופל ממפלצות ברמה 72–108 — בערך 10% בזמן שהקווסט פעיל. לדוגמה Tarkan (Mutant, Bloody Wolf, Beam Knight, Death Beam Knight, Tantalos), Aida (Death Rider, Witch Queen), Crywolf (Lance Scout).",
      },
    },

    {
      type: "h",
      en: "Quest 3 — Dark Stone (Combo)",
      he: "קווסט 3 — Dark Stone (Combo)",
    },
    {
      type: "p",
      en: "Pay 20,000 Zen. Hunt monsters level 72–108 until a Dark Stone drops, then return it to Marlon to unlock the Combo skill. Same useful maps as Ring of Honor: Tarkan (Mutant, Bloody Wolf, Beam Knight, Death Beam Knight, Tantalos), Aida (Death Rider, Witch Queen), Crywolf (Lance Scout).",
      he: "שלמו 20,000 Zen. צודו מפלצות ברמה 72–108 עד ש־Dark Stone נופל, ואז החזירו אותו ל־Marlon כדי לפתוח את מיומנות ה־Combo. אותן מפות שימושיות כמו Ring of Honor: Tarkan (Mutant, Bloody Wolf, Beam Knight, Death Beam Knight, Tantalos), Aida (Death Rider, Witch Queen), Crywolf (Lance Scout).",
    },
    {
      type: "itemcard",
      img: guideImg.darkStone,
      name: "Dark Stone",
      qty: "×1",
      note: {
        en: "Drops from monsters level 72–108 — about 10% while the quest is active. Same maps as Ring of Honor (Tarkan, Aida, Crywolf).",
        he: "נופל ממפלצות ברמה 72–108 — בערך 10% בזמן שהקווסט פעיל. אותן מפות כמו Ring of Honor (Tarkan, Aida, Crywolf).",
      },
    },
    {
      type: "note",
      en: "Dark Knight (DK) must finish Quest 3 (Combo) before starting Apostle Devin. Dark Wizard (DW), Fairy Elf (FE) and Summoner (SU) need Quest 2 (Hero) finished for the Devin Evidence stage.",
      he: "Dark Knight (DK) חייב לסיים את קווסט 3 (Combo) לפני שמתחילים אצל Apostle Devin. Dark Wizard (DW), Fairy Elf (FE) ו־Summoner (SU) צריכים לסיים את קווסט 2 (Hero) עבור שלב ה־Evidence של Devin.",
    },
  ],

  "class-3": [
    {
      type: "h",
      en: "Third Class Change — Apostle Devin",
      he: "שינוי מחלקה שלישי — Apostle Devin",
    },
    {
      type: "p",
      en: "The third class change is a multi-stage quest chain from Apostle Devin: collect Evidence, clear Balgass' Barracks, then clear the Gate to the Refuge. Follow the stages in order.",
      he: "שינוי המחלקה השלישי הוא שרשרת קווסטים רב־שלבית מ־Apostle Devin: איסוף Evidence, ניקוי Balgass' Barracks, ואז ניקוי ה־Gate אל ה־Refuge. עקבו אחרי השלבים לפי הסדר.",
    },
    {
      type: "npc",
      img: guideImg.npcDevin,
      name: "Apostle Devin",
      map: { en: "Crywolf", he: "Crywolf" },
      coords: "228, 48",
    },
    {
      type: "note",
      en: "Start requirements: Dark Wizard / Dark Knight / Fairy Elf / Summoner need 2nd class (ChangeUp ≥ 1) and Level 380+. Magic Gladiator / Dark Lord / Rage Fighter need Level 380+ only (no 2nd class — they go ChangeUp 0 → 2). The Barracks stage additionally requires Level 400+.",
      he: "דרישות התחלה: Dark Wizard / Dark Knight / Fairy Elf / Summoner צריכים מחלקה שנייה (ChangeUp ≥ 1) ו־Level 380+. Magic Gladiator / Dark Lord / Rage Fighter צריכים Level 380+ בלבד (ללא מחלקה שנייה — הם עוברים ChangeUp 0 → 2). שלב ה־Barracks דורש בנוסף Level 400+.",
    },

    {
      type: "timeline",
      steps: [
        {
          title: { en: "Stage 1 — Evidence", he: "שלב 1 — Evidence" },
          body: {
            en: "Pay 5,000,000 Zen and collect three Evidence items, then return to Devin.",
            he: "שלמו 5,000,000 Zen ואספו שלושה פריטי Evidence, ואז חזרו אל Devin.",
          },
          img: guideImg.npcDevin,
        },
        {
          title: {
            en: "Accept the next stage",
            he: "קבלו את השלב הבא",
          },
          body: {
            en: "After the Evidence, accept the next quest from Devin BEFORE going anywhere else.",
            he: "אחרי ה־Evidence, קבלו את הקווסט הבא מ־Devin לפני שהולכים למקום אחר.",
          },
          img: guideImg.npcDevin,
        },
        {
          title: {
            en: "Stage 2 — Balgass' Barracks",
            he: "שלב 2 — Balgass' Barracks",
          },
          body: {
            en: "See Werewolf Quarel, pay 3,000,000 Zen, and clear the Barracks monsters.",
            he: "פנו אל Werewolf Quarel, שלמו 3,000,000 Zen, ונקו את מפלצות ה־Barracks.",
          },
          img: guideImg.npcWerewolf,
        },
        {
          title: {
            en: "Stage 3 — Gate & Refuge",
            he: "שלב 3 — Gate ו־Refuge",
          },
          body: {
            en: "Accept the Gate stage (100,000 Zen), use the Gatekeeper, and clear the Refuge.",
            he: "קבלו את שלב ה־Gate (100,000 Zen), השתמשו ב־Gatekeeper, ונקו את ה־Refuge.",
          },
          img: guideImg.npcGatekeeper,
        },
        {
          title: { en: "Third Class granted", he: "מחלקה שלישית מוענקת" },
          body: {
            en: "Return to Devin to receive your third class.",
            he: "חזרו אל Devin כדי לקבל את המחלקה השלישית.",
          },
          img: guideImg.npcDevin,
        },
      ],
    },

    {
      type: "h",
      en: "Stage 1 — Evidence",
      he: "שלב 1 — Evidence",
    },
    {
      type: "p",
      en: "Pay 5,000,000 Zen to start, then collect all three Evidence items (about 10% each while the quest is active) and return to Devin.",
      he: "שלמו 5,000,000 Zen כדי להתחיל, אספו את שלושת פריטי ה־Evidence (בערך 10% כל אחד בזמן שהקווסט פעיל) וחזרו אל Devin.",
    },
    {
      type: "itemcard",
      img: guideImg.deathbeamFlame,
      name: "Death-beam Knight Flame",
      qty: "×1",
      note: {
        en: "Drops from Death Beam Knight in Tarkan — about 10% while the quest is active.",
        he: "נופל מ־Death Beam Knight ב־Tarkan — בערך 10% בזמן שהקווסט פעיל.",
      },
    },
    {
      type: "itemcard",
      img: guideImg.hellminerHorn,
      name: "Hell-Miner Horn",
      qty: "×1",
      note: {
        en: "Drops from Hell Maine in Aida — about 10% while the quest is active.",
        he: "נופל מ־Hell Maine ב־Aida — בערך 10% בזמן שהקווסט פעיל.",
      },
    },
    {
      type: "itemcard",
      img: guideImg.phoenixFeather,
      name: "Dark Phoenix Feather",
      qty: "×1",
      note: {
        en: "Drops from Phoenix of Darkness in Icarus — about 10% while the quest is active.",
        he: "נופל מ־Phoenix of Darkness ב־Icarus — בערך 10% בזמן שהקווסט פעיל.",
      },
    },
    {
      type: "warn",
      en: "After you finish the Evidence, you MUST accept the next stage from Devin before going to the Werewolf. Finishing the Evidence alone is not enough.",
      he: "אחרי שסיימתם את ה־Evidence, חובה לקבל את השלב הבא מ־Devin לפני שהולכים ל־Werewolf. סיום ה־Evidence לבדו אינו מספיק.",
    },

    {
      type: "h",
      en: "Stage 2 — Balgass' Barracks (Werewolf Quarel)",
      he: "שלב 2 — Balgass' Barracks (Werewolf Quarel)",
    },
    {
      type: "p",
      en: "With the Barracks stage accepted from Devin, go to Werewolf Quarel. You need Level 400+ and pay 3,000,000 Zen to enter Balgass' Barracks. Clear the monsters below, then return to Devin.",
      he: "אחרי שקיבלתם את שלב ה־Barracks מ־Devin, לכו אל Werewolf Quarel. צריך Level 400+ ולשלם 3,000,000 Zen כדי להיכנס ל־Balgass' Barracks. נקו את המפלצות למטה, ואז חזרו אל Devin.",
    },
    {
      type: "npc",
      img: guideImg.npcWerewolf,
      name: "Werewolf Quarel (Werewolf Guardsman)",
      map: { en: "Crywolf", he: "Crywolf" },
      coords: "62, 239",
    },
    {
      type: "warn",
      en: "You must accept the Barracks stage from Devin first. Werewolf Quarel will only help after that stage is accepted.",
      he: "חובה לקבל קודם את שלב ה־Barracks מ־Devin. Werewolf Quarel יעזור רק אחרי שהשלב הזה התקבל.",
    },
    {
      type: "itemcard",
      img: guideImg.monBalram,
      name: "Balram",
      qty: "×20",
      variant: "monster",
      note: {
        en: "Kill inside Balgass' Barracks.",
        he: "הרגו בתוך Balgass' Barracks.",
      },
    },
    {
      type: "itemcard",
      img: guideImg.monDeathSpirit,
      name: "Death Spirit",
      qty: "×20",
      variant: "monster",
      note: {
        en: "Kill inside Balgass' Barracks.",
        he: "הרגו בתוך Balgass' Barracks.",
      },
    },
    {
      type: "itemcard",
      img: guideImg.monSoram,
      name: "Soram",
      qty: "×20",
      variant: "monster",
      note: {
        en: "Kill inside Balgass' Barracks.",
        he: "הרגו בתוך Balgass' Barracks.",
      },
    },

    {
      type: "h",
      en: "Stage 3 — Gate & Refuge (Gatekeeper)",
      he: "שלב 3 — Gate ו־Refuge (Gatekeeper)",
    },
    {
      type: "p",
      en: "Return to Devin and accept the Gate stage (100,000 Zen). Go to the Gatekeeper to enter the Refuge, kill one Dark Elf, then return to Devin to receive your third class.",
      he: "חזרו אל Devin וקבלו את שלב ה־Gate (100,000 Zen). לכו אל ה־Gatekeeper כדי להיכנס ל־Refuge, הרגו Dark Elf אחד, ואז חזרו אל Devin כדי לקבל את המחלקה השלישית.",
    },
    {
      type: "npc",
      img: guideImg.npcGatekeeper,
      name: "Gatekeeper",
      map: { en: "Balgass' Barracks", he: "Balgass' Barracks" },
      coords: "119, 168",
    },
    {
      type: "itemcard",
      img: guideImg.monDarkElf,
      name: "Dark Elf",
      qty: "×1",
      variant: "monster",
      note: {
        en: "Kill inside the Refuge.",
        he: "הרגו בתוך ה־Refuge.",
      },
    },

    {
      type: "h",
      en: "Third Class results",
      he: "תוצאות המחלקה השלישית",
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classDwT2,
          name: { en: "Soul Master", he: "Soul Master" },
          label: { en: "2nd", he: "2nd" },
        },
        {
          img: guideImg.classDwT3,
          name: { en: "Grand Master", he: "Grand Master" },
          label: { en: "3rd", he: "3rd" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classDkT2,
          name: { en: "Blade Knight", he: "Blade Knight" },
          label: { en: "2nd", he: "2nd" },
        },
        {
          img: guideImg.classDkT3,
          name: { en: "Blade Master", he: "Blade Master" },
          label: { en: "3rd", he: "3rd" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classFeT2,
          name: { en: "Muse Elf", he: "Muse Elf" },
          label: { en: "2nd", he: "2nd" },
        },
        {
          img: guideImg.classFeT3,
          name: { en: "High Elf", he: "High Elf" },
          label: { en: "3rd", he: "3rd" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classSuT2,
          name: { en: "Bloody Summoner", he: "Bloody Summoner" },
          label: { en: "2nd", he: "2nd" },
        },
        {
          img: guideImg.classSuT3,
          name: { en: "Dimension Master", he: "Dimension Master" },
          label: { en: "3rd", he: "3rd" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classMgT1,
          name: { en: "Magic Gladiator", he: "Magic Gladiator" },
          label: { en: "Base", he: "בסיס" },
        },
        {
          img: guideImg.classMgT3,
          name: { en: "Duel Master", he: "Duel Master" },
          label: { en: "3rd", he: "3rd" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classDlT1,
          name: { en: "Dark Lord", he: "Dark Lord" },
          label: { en: "Base", he: "בסיס" },
        },
        {
          img: guideImg.classDlT3,
          name: { en: "Lord Emperor", he: "Lord Emperor" },
          label: { en: "3rd", he: "3rd" },
        },
      ],
    },
    {
      type: "classevo",
      stages: [
        {
          img: guideImg.classRfT1,
          name: { en: "Rage Fighter", he: "Rage Fighter" },
          label: { en: "Base", he: "בסיס" },
        },
        {
          img: guideImg.classRfT3,
          name: { en: "Fist Master", he: "Fist Master" },
          label: { en: "3rd", he: "3rd" },
        },
      ],
    },
    {
      type: "table",
      columns: [
        { key: "from", en: "Class", he: "מחלקה" },
        { key: "to", en: "Third Class", he: "מחלקה שלישית" },
      ],
      rows: [
        { from: "Dark Wizard (DW)", to: "Grand Master" },
        { from: "Dark Knight (DK)", to: "Blade Master" },
        { from: "Fairy Elf (FE)", to: "High Elf" },
        { from: "Summoner (SU)", to: "Dimension Master" },
        { from: "Magic Gladiator (MG)", to: "Duel Master" },
        { from: "Dark Lord (DL)", to: "Lord Emperor" },
        { from: "Rage Fighter (RF)", to: "Fist Master" },
      ],
    },
  ],
};

export const classChangeTopicIds = Object.keys(guideClassChangeContent);

export default guideClassChangeContent;
